import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import PersonChallengeCard from '@/components/PersonChallengeCard/PersonChallengeCard';
import { Color } from '@/constants/color';
import { STICKY_CHROME_CLASS } from '@/constants/layout';
import { usePersonChallengesByIdPaginated } from '@/hooks/useChallenge';
import usePagination from '@/hooks/usePagination';
import PersonContext from '@/pages/Person/PersonContext';
import PersonChallengeRepository, {
  type PersonChallengeParams
} from '@/repositories/PersonChallengeRepository';
import { PersonChallenge as ChallengeType } from '@/types/person_challenge';
import Button from '@/ui/components/Button';
import Container from '@/ui/components/Container';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';

const PersonChallenge: React.FC = () => {
  const { person } = useContext(PersonContext);
  const [searchParams, setSearchParams] = useSearchParams({ page: '1' });
  const [totalPages, setTotalPages] = useState<number>(0);
  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    +(searchParams.get('page') ?? '1'),
    totalPages
  );

  const [params, setParams] = useState<PersonChallengeParams>({
    page: '1'
  });

  const getTriStateButton = (label: string, value?: boolean) => {
    if (value === true) {
      return { color: Color.GREEN, text: label };
    }

    if (value === false) {
      return { color: Color.RED, text: label };
    }

    return { color: Color.WHITE, text: label };
  };

  const getTriStateTooltip = (label: string, value?: boolean) => {
    if (value === true) {
      return `Including only challenges that are ${label.toLowerCase()}`;
    }

    if (value === false) {
      return `Excluding challenges that are ${label.toLowerCase()}`;
    }

    return `Filter not applied`;
  };

  const getTriStateStateText = (value?: boolean) => {
    if (value === true) {
      return 'Include';
    }

    if (value === false) {
      return 'Exclude';
    }

    return 'Any';
  };

  const cycleTriStateFilter = (filterKey: 'assigned' | 'recurring') => {
    setParams((currentParams) => {
      const newParams = { ...currentParams };
      const currentValue = currentParams[filterKey];

      if (currentValue === undefined) {
        newParams[filterKey] = true;
      } else if (currentValue === true) {
        newParams[filterKey] = false;
      } else {
        delete newParams[filterKey];
      }

      return newParams;
    });
  };

  const toggleFilter = (filterKey: 'finished' | 'not_finished', value: boolean) => {
    setParams((currentParams) => {
      const newParams = { ...currentParams };

      if (filterKey === 'finished') {
        if (value) {
          newParams.finished = true;
          delete newParams.not_finished;
        } else {
          delete newParams.finished;
          // If not_finished is also false, don't add it back automatically
          if (!newParams.not_finished) {
            newParams.not_finished = true;
          }
        }
      } else if (filterKey === 'not_finished') {
        if (value) {
          newParams.not_finished = true;
          delete newParams.finished;
        } else {
          delete newParams.not_finished;
          // If finished is also false, don't add it back automatically
          if (!newParams.finished) {
            newParams.finished = true;
          }
        }
      }

      // Special case: if both finished and not_finished are true, remove both
      if (newParams.finished && newParams.not_finished) {
        delete newParams.finished;
        delete newParams.not_finished;
      }

      return newParams;
    });
  };

  const {
    data: challengesResponse,
    isPending,
    isSuccess
  } = usePersonChallengesByIdPaginated(params, person.id);

  const repository = PersonChallengeRepository();
  const [isClearingCache, setIsClearingCache] = useState(false);
  const assignedFilter = getTriStateButton('Assigned', params.assigned);
  const recurringFilter = getTriStateButton('Recurring', params.recurring);
  const assignedTooltip = getTriStateTooltip('Assigned', params.assigned);
  const recurringTooltip = getTriStateTooltip('Recurring', params.recurring);
  const assignedStateText = getTriStateStateText(params.assigned);
  const recurringStateText = getTriStateStateText(params.recurring);
  const hasAnyChallengeFilter =
    params.finished !== undefined ||
    params.not_finished !== undefined ||
    params.assigned !== undefined ||
    params.recurring !== undefined;

  useEffect(() => {
    if (isSuccess) {
      if (challengesResponse?.data?.page) {
        setSearchParams(
          (currentParams) => {
            currentParams.set('page', challengesResponse.data.page.toString());
            return currentParams;
          },
          { replace: true }
        );
      }

      if (challengesResponse?.data?.total_pages) {
        setTotalPages(challengesResponse.data.total_pages);
      }
    }
  }, [isSuccess, challengesResponse, setSearchParams]);

  // Replaces rather than pushes: the page is recording where the reader already
  // is, so a history entry here is one the Back button has to be pressed through
  // before it reaches wherever they came from.
  useEffect(() => {
    setSearchParams(
      (currentParams) => {
        currentParams.set('page', currentPage.toString());
        return currentParams;
      },
      { replace: true }
    );
  }, [currentPage, setSearchParams]);

  useEffect(() => {
    setParams((currentParams: PersonChallengeParams) => ({
      ...currentParams,
      page: currentPage.toString()
    }));
  }, [currentPage]);

  return (
    <Container type="page">
      <PageHeader headingText="Challenges" className="sr-only" />

      {/* Filter Buttons */}
      <div
        className={`border-b border-gray-200 py-3 z-19 bg-gray-100 ${STICKY_CHROME_CLASS.pageSection}`}>
        <div className="flex flex-wrap gap-2">
          <Button
            size="xs"
            color={!hasAnyChallengeFilter ? Color.BLUE : Color.WHITE}
            onClick={() =>
              setParams((currentParams) => {
                const newParams = { ...currentParams };
                delete newParams.finished;
                delete newParams.not_finished;
                delete newParams.assigned;
                delete newParams.recurring;
                return newParams;
              })
            }>
            All
          </Button>

          <Button
            size="xs"
            color={params.not_finished ? Color.BLUE : Color.WHITE}
            onClick={() => toggleFilter('not_finished', !params.not_finished)}>
            Not Finished
          </Button>

          <Button
            size="xs"
            color={params.finished ? Color.BLUE : Color.WHITE}
            onClick={() => toggleFilter('finished', !params.finished)}>
            Finished
          </Button>

          <Button
            size="xs"
            color={assignedFilter.color}
            ariaLabel={`Assigned filter state: ${assignedStateText}`}
            title={assignedTooltip}
            onClick={() => cycleTriStateFilter('assigned')}>
            {`${assignedFilter.text} (${assignedStateText})`}
          </Button>

          <Button
            size="xs"
            color={recurringFilter.color}
            ariaLabel={`Recurring filter state: ${recurringStateText}`}
            title={recurringTooltip}
            onClick={() => cycleTriStateFilter('recurring')}>
            {`${recurringFilter.text} (${recurringStateText})`}
          </Button>

          <Button
            size="xs"
            color={Color.RED}
            onClick={async () => {
              setIsClearingCache(true);
              try {
                await repository.clearCache({ id: person.id, ...params });
              } finally {
                setIsClearingCache(false);
              }
            }}
            disabled={isClearingCache}>
            {isClearingCache ? 'Clearing...' : 'Clear Cache'}
          </Button>
        </div>
      </div>

      <div className="border-t-2 border-gray-200 py-2">
        <Pagination
          currentPage={currentPage}
          goToPage={goToPage}
          nextPage={nextPage}
          prevPage={prevPage}
          totalPages={totalPages}>
          {isPending ? (
            <div className="my-2">
              <LoadingSkeleton spinnerProps={{ label: 'Loading Challenges' }} />
            </div>
          ) : !challengesResponse?.data?.results?.length ? (
            <div className="flex-1 flex items-center justify-center">
              <span className="text-red-500">There are no challenges available.</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {challengesResponse.data.results.map((challenge: ChallengeType, index: number) => (
                <PersonChallengeCard key={`${index}-challenge`} challenge={challenge} />
              ))}
            </div>
          )}
        </Pagination>
      </div>
    </Container>
  );
};

export default PersonChallenge;
